import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { formatDate, formatMoney } from "./walletFormat";

// Artist Studio > Music Wallet > Royalty statements (FSD 6.5.2): one row per month that was paid, newest first. Open a month to see
// the streams and the money of each track. Streams of a track under an open copyright notice are shown as "held": the money is
// paid into the wallet once the notice is closed. The amounts are the ones credited to the wallet, in minor units from the API.

const count = (n) => Number(n || 0).toLocaleString("en-US");

function MonthDetail({ month }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    walletApi.royaltyStatement(month).then(
      (r) => { if (live) setData(r); },
      (e) => { if (live) setError(walletErrorMessage(e, WALLET_MESSAGES.royaltyStatement)); }
    );
    return () => { live = false; };
  }, [month]);

  if (error) return <p className="px-6 pb-5 text-sm text-red-700" role="alert" data-testid="statement-error">{error}</p>;
  if (!data) return <div className="flex justify-center pb-5"><Loading /></div>;
  return (
    <div className="px-6 pb-5" data-testid="statement-detail">
      <p className="mb-3 text-sm text-gray-600">{data.memo}{data.posted_at ? `, paid into your wallet on ${formatDate(data.posted_at)}` : ""}.</p>
      <table className="w-full text-left text-[14px]">
        <thead className="text-xs font-bold uppercase tracking-wide text-gray-500">
          <tr><th className="py-2 pr-4">Track</th><th className="py-2 pr-4 text-right">Streams</th><th className="py-2 text-right">Royalty</th></tr>
        </thead>
        <tbody>
          {data.tracks.map((t) => (
            <tr key={t.track_id} className="border-t border-white/80" data-testid="statement-track">
              <td className="py-2 pr-4 text-gray-900">{t.title || `Track ${t.track_id}`}</td>
              <td className="py-2 pr-4 text-right text-gray-700">{count(t.streams)}</td>
              <td className="py-2 text-right font-semibold text-gray-900">
                {formatMoney(t.amount_minor, data.currency)}
                {t.status === "held" && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800" data-testid="statement-held">Held</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.held_minor > 0 && (
        <p className="mt-3 text-sm text-amber-800" data-testid="statement-held-note">
          {formatMoney(data.held_minor, data.currency)} is held because of an open copyright notice. It is paid into your wallet when the notice is closed.
        </p>
      )}
    </div>
  );
}

function StatementsBody() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const latest = useRef(0);

  const load = useCallback(() => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setError("");
    setRows(null);
    walletApi.royaltyStatements({ limit: 24 }).then(
      (r) => { if (ticket === latest.current) setRows(r?.statements || []); },
      (e) => {
        if (ticket !== latest.current) return;
        setError(walletErrorMessage(e, WALLET_MESSAGES.royaltyStatements));
        setRows([]);
      }
    );
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="royalty-statements">
      <Link to="/artist-studio/wallet" className="text-sm font-semibold text-gray-700 hover:underline">← Back to Music Wallet</Link>
      <h1 className="mt-4 text-[32px] font-bold leading-tight text-gray-900">Royalty statements</h1>
      <p className="mt-1 text-[15px] text-gray-600">Your streaming royalties are calculated once a month, from the valid streams of the month before.</p>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="statements-error">
          {error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
        </p>
      )}

      <div className="mt-5 rounded-2xl bg-[#f1f6f2]">
        {rows === null ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : rows.length === 0 ? (
          !error && <p className="p-8 text-center text-gray-500" data-testid="statements-empty">No statements yet. The first one appears after the first monthly run (around the 5th of the month).</p>
        ) : (
          rows.map((s) => (
            <div key={s.month} className="border-t border-white/70 first:border-t-0" data-testid="statement-row">
              <button
                type="button"
                className="flex w-full flex-wrap items-center justify-between gap-3 px-6 py-4 text-left"
                aria-expanded={open === s.month}
                onClick={() => setOpen(open === s.month ? null : s.month)}
              >
                <span className="text-[16px] font-semibold text-gray-900">{s.label}</span>
                <span className="text-sm text-gray-600">{count(s.streams)} streams · {s.tracks} {s.tracks === 1 ? "track" : "tracks"}</span>
                <span className="text-[16px] font-bold" style={{ color: ACCENT }}>
                  {formatMoney(s.amount_minor, s.currency)}
                  {s.held_minor > 0 && <span className="ml-2 text-sm font-semibold text-amber-800">+ {formatMoney(s.held_minor, s.currency)} held</span>}
                </span>
              </button>
              {open === s.month && <MonthDetail month={s.month} />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function RoyaltyStatements() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="wallet" heading="" tabs={[]} maxWidth="max-w-4xl">
      <StatementsBody />
    </StudioGate>
  );
}
